/**
 * Job timings, as two clocks and a set of shifts.
 *
 * Lifted out of JobsPage, which had these controls first, because the client
 * page's requirement form now asks the same question. One definition rather
 * than two: the column holds a sentence, and two screens composing that
 * sentence differently is how "9:00 AM to 6:00 PM · Night shift" and
 * "9-6, rotational" end up in the same column meaning the same thing.
 *
 * The composed string is what gets stored, deliberately. `work_time` is prose
 * that a recruiter reads out to a candidate, and structuring it into three
 * columns would buy a query nobody runs at the cost of a sentence that no
 * longer reads like one.
 */

/** The shifts a client asks for, as the spec lists them. */
export const SHIFT_KINDS = ['Day shift', 'Night shift', 'Rotational shift'];

/** A 24-hour input value as the 12-hour clock the desk speaks in. */
export function clockLabel(value: string): string {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h)) return value;
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m ?? 0).padStart(2, '0')} ${suffix}`;
}

/**
 * The hours and the shifts as one line.
 *
 * Hours and shifts are independent: a client may give one, the other, or both,
 * and each part is omitted rather than filled with a placeholder. Null when
 * neither is present, because an empty string would be a work time of ""
 * rather than no work time recorded.
 */
export function composeWorkTime(
  from: string,
  to: string,
  shifts: string[],
): string | null {
  const hours =
    from && to ? `${clockLabel(from)} to ${clockLabel(to)}`
    : from ? `from ${clockLabel(from)}`
    : to ? `until ${clockLabel(to)}`
    : '';
  const parts = [hours, shifts.join(', ')].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Which shifts a stored work time mentions.
 *
 * So a form reopening on an existing requirement starts with the right boxes
 * ticked. Substring matching on the word rather than the full label, because
 * this column holds 119 rows of prose written before the checkboxes existed
 * and "9-6, rotational" should still tick Rotational.
 */
export function shiftsFrom(stored: string | null | undefined): string[] {
  const text = (stored ?? '').toLowerCase();
  return SHIFT_KINDS.filter((kind) => text.includes(kind.split(' ')[0].toLowerCase()));
}
